<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DLoading,
  SearchMdIcon,
  Edit03Icon,
  Trash03Icon,
  Lock03Icon,
  LockUnlocked03Icon
} from 'front-library'
import {
  getTurns,
  Turn,
  deleteTurn,
  updateStateTurn
} from '@/app/core/services/turns'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const acceptQuestion = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const search = ref()
const tableTurn = ref(null)
const TurnForDelete = ref()
const isDelete = ref(true)
const turnUpdateState = ref()

/* Datos componente table */
const columns = ref([
  { name: 'code', title: t('turns.list.code'), table: 'code' },
  { name: 'name', title: t('turns.list.name'), table: 'name' },
  {
    name: 'period_days',
    title: t('turns.list.periodDays'),
    table: 'period_days'
  },
  { name: 'sucursal', title: t('turns.list.sucursal'), table: 'sucursal' },
  { name: 'state', title: t('turns.list.status'), table: 'state' },
  {
    name: 'updated_at',
    title: t('turns.list.lastUpdate'),
    table: 'updated_at'
  },
  { name: 'actions', title: '' }
])
const rows = ref<Turn[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createTurn = () => {
  void router.push({ name: 'CreateTurn' })
}
const updateTurn = (id: any) => {
  void router.push({ name: 'UpdateTurn', params: { id } })
}
const getTurn = (id: any) => {
  void router.push({ name: 'GetTurn', params: { id } })
}

/* Cambiar estado del turno */
const changeStateTurn = (values: any) => {
  typeAlertModal.value = 'question'
  const text = values.is_inactive
    ? t('table.action.activate')
    : t('table.action.deactivate')
  turnUpdateState.value = [
    { name: values.name, id: values.id, is_inactive: values.is_inactive }
  ]
  titleModal.value = t('turns.list.actionStatus', {
    status: text,
    turn: values.name as string
  })
  descriptionModal.value = ''
  acceptQuestion.value = t('modal.acceptQuestion', { action: text })
  showModal.value = true
  isDelete.value = false
}
const changeStateConfirm = async () => {
  showModal.value = false
  await updateStateTurn(
    turnUpdateState.value[0].id,
    !turnUpdateState.value[0].is_inactive
  )
    .then(response => {
      notifications.addNotification({
        title: !turnUpdateState.value[0].is_inactive
          ? t('notification.disabled')
          : t('notification.activated'),
        text: t('turns.notification.changeStatus', {
          location: turnUpdateState.value[0].name as string,
          status: !turnUpdateState.value[0].is_inactive
            ? t('table.action.deactivated')
            : t('table.action.asset')
        }),
        type: 'success',
        time: 10000
      })
      void turns()
    })
    .catch((error: any) => {
      notificationError(error, 'turns', t, notifications, loading)
    })
}

/* Eliminar turno */
const removeTurn = (values: any) => {
  typeAlertModal.value = 'question'
  titleModal.value = t('turns.list.actionStatus', {
    status: t('table.action.delete'),
    turn: values.name as string
  })
  descriptionModal.value = t('modal.description')
  acceptQuestion.value = t('modal.acceptQuestion', {
    action: t('table.action.delete')
  })
  showModal.value = true
  TurnForDelete.value = values.id
  isDelete.value = true
}
const deleteConfirm = async () => {
  loading.value = true
  showModal.value = false
  await deleteTurn(TurnForDelete.value)
    .then(response => {
      loading.value = false
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('turns.notification.action', [t('notification.wasRemoved')]),
        type: 'success',
        time: 10000
      })
      void turns()
    })
    .catch((error: any) => {
      notificationError(error, 'turns', t, notifications, loading)
    })
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
}

/* Listar turnos */
const turns = async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const data: any = await getTurns(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data.results
    totalRows.value = data.totalItems
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'turns', t, notifications, loading)
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <DModal
    :open="showModal"
    :typeAlert="typeAlertModal"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="isDelete ? deleteConfirm() : changeStateConfirm()" />
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('turns.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('turns.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="turns" />
          <DBtn @click.prevent="turns" v-html="t('form.button.search')" />
        </div>
        <DBtn
          :icon="'PlusIcon'"
          type="button"
          @click="createTurn()"
          v-if="store.hasPermission('turn:create')">
          {{ t('form.button.create', { form: t('turns.list.title', 0) }) }}
        </DBtn>
      </div>
    </div>
    <DTable
      ref="tableTurn"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), turns()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), turns()">
      <template #state="data: any">
        <div
          class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
          :class="
            data.is_inactive
              ? 'bg-red-100 text-red-600'
              : 'bg-green-100 text-green-600'
          ">
          <div
            class="mr-2 w-1.5 h-1.5 rounded-full"
            :class="data.is_inactive ? 'bg-red-600' : 'bg-green-600'"></div>

          {{
            data.is_inactive
              ? t('table.action.inactive')
              : t('table.action.active')
          }}
        </div>
      </template>
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('turn:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div class="bg-gray-50 rounded-md py-2" @click="getTurn(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('turn:update') && !data.is_inactive"
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateTurn(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('turn:enable')"
            arrow
            offsetDistance="12"
            :content="
              data.is_inactive
                ? t('turns.list.activate')
                : t('turns.list.deactivate')
            "
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="changeStateTurn(data)">
              <LockUnlocked03Icon
                v-if="data.is_inactive"
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
              <Lock03Icon
                v-else
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('turn:delete') && data.is_inactive"
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div class="bg-gray-50 rounded-md py-2" @click="removeTurn(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
